import type { OfferImpressionEmission, OfferImpressionResolution } from "./OfferImpression.emit";

interface OfferImpressionEmitterModule {
  resolveOfferImpressionEmission(input: OfferImpressionEmission): OfferImpressionResolution;
}

export type OfferImpressionEmitterLoader = () => Promise<OfferImpressionEmitterModule>;
export type OfferImpressionConsumer = (resolution: OfferImpressionResolution) => void;

let emitterModulePromise: Promise<OfferImpressionEmitterModule> | null = null;
const pendingDeliveries = new Map<string, OfferImpressionEmission>();
let pageHideTelemetryInstalled = false;

function defaultEmitterLoader(): Promise<OfferImpressionEmitterModule> {
  emitterModulePromise ??= import("./OfferImpression.emit").catch((error: unknown) => {
    emitterModulePromise = null;
    throw error;
  });
  return emitterModulePromise;
}

function dispatchDeliveryError(
  error: unknown,
  input: OfferImpressionEmission,
  boundary: string
): void {
  const source = error instanceof Error ? error : new Error(String(error));
  const reported = Object.assign(new Error(source.message), {
    name: source.name,
    stack: source.stack,
    ishvaramBoundary: boundary,
    ishvaramComponent: `placement:${input.placement}`,
  });
  window.dispatchEvent(new ErrorEvent("error", { error: reported, message: reported.message }));
}

function reportPendingNavigationDelivery(): void {
  const pending = pendingDeliveries.values().next().value;
  if (!pending) return;
  const error = new Error("Offer impression delivery pending during page navigation");
  error.name = "OfferImpressionDeliveryPending";
  dispatchDeliveryError(error, pending, "offer_impression_delivery_navigation");
}

function ensurePageHideTelemetry(): void {
  if (pageHideTelemetryInstalled || typeof window === "undefined") return;
  pageHideTelemetryInstalled = true;
  window.addEventListener("pagehide", reportPendingNavigationDelivery, { capture: true });
}

function recoverEmitterChunk(error: unknown, input: OfferImpressionEmission): void {
  // GlobalErrorReporter owns the js_error beacon. ChunkErrorReloader recognizes
  // this dynamic-import message and invokes the shared loop-guarded recovery
  // state machine, without duplicating either dependency into every route.
  dispatchDeliveryError(error, input, "offer_impression_delivery_import");
}

export function preloadOfferImpressionEmitter(): void {
  void defaultEmitterLoader().catch(() => undefined);
}

/** Return true only after validation and telemetry emission completed. */
export async function deliverOfferImpression(
  input: OfferImpressionEmission,
  consume: OfferImpressionConsumer,
  loader: OfferImpressionEmitterLoader = defaultEmitterLoader
): Promise<boolean> {
  ensurePageHideTelemetry();
  pendingDeliveries.set(input.presentationId, input);
  let emitter: OfferImpressionEmitterModule;
  try {
    emitter = await loader();
  } catch (error) {
    recoverEmitterChunk(error, input);
    pendingDeliveries.delete(input.presentationId);
    return false;
  }
  try {
    consume(emitter.resolveOfferImpressionEmission(input));
    return true;
  } catch (error) {
    dispatchDeliveryError(error, input, "offer_impression_delivery_emit");
    return false;
  } finally {
    pendingDeliveries.delete(input.presentationId);
  }
}

/** Test-only reset for module-level delivery state. */
export function resetOfferImpressionDeliveryForTests(): void {
  emitterModulePromise = null;
  pendingDeliveries.clear();
}
